<template>
  <div class="bg-white rounded-2xl p-7 border border-[#1c4848]/10 shadow-sm" :class="[wrapperClass]">
    <div class="flex items-center justify-between mb-6">
      <div>
        <h3 class="text-[20px] font-medium tracking-tight text-[#1c4848]">{{ title }}</h3>
        <p class="text-[15px] text-[#1c4848]/55 mt-1">
          {{ subtitle }}
        </p>
      </div>
      <span
        v-if="worstStep"
        class="text-[13px] font-medium px-2 py-1 rounded-lg text-amber-600 bg-amber-50"
        :title="`Biggest drop-off happens here`"
      >
        Drop: {{ worstStep.label }}
      </span>
    </div>

    <p v-if="!steps.length" class="text-base text-[#1c4848]/40 py-8 text-center">
      No events recorded in this period yet.
    </p>

    <div v-else class="space-y-4">
      <div v-for="(step, index) in steps" :key="step.key">
        <div class="flex items-end justify-between mb-1.5">
          <span class="text-lg font-medium text-[#1c4848]">{{ step.label }}</span>
          <span class="text-[19px] font-semibold text-[#1c4848]">
            {{ formatNumber(step.users) }}
            <span class="text-[13px] font-medium text-[#1c4848]/45 ml-1">
              {{ step.conversion_from_start }}%
            </span>
          </span>
        </div>
        <div class="w-full h-4 rounded-full bg-[#1c4848]/5 overflow-hidden">
          <div
            class="h-full rounded-full transition-all duration-500"
            :style="{
              width: `${Math.max(barWidth(step), 1)}%`,
              backgroundColor: index === 0 ? '#1c4848' : shade(index),
            }"
          />
        </div>
        <p
          v-if="index > 0"
          class="text-[13px] font-semibold mt-1"
          :class="step.conversion_from_previous < 40 ? 'text-amber-600' : 'text-[#1c4848]/30'"
        >
          {{ step.conversion_from_previous }}% of previous step
        </p>
      </div>
    </div>
  </div>
</template>

<script setup>
import { computed } from 'vue';

const props = defineProps({
  title: { type: String, default: 'Conversion Funnel' },
  subtitle: { type: String, default: 'Install to subscription' },
  steps: { type: Array, default: () => [] },
  wrapperClass: { type: String, default: '' },
});

/** Bars are drawn relative to the first step so the shape reads as a funnel. */
const firstStepUsers = computed(() => props.steps[0]?.users || 0);

function barWidth(step) {
  if (!firstStepUsers.value) return 0;
  return (step.users / firstStepUsers.value) * 100;
}

/** Later steps fade toward the gold accent, so the eye follows the drop. */
function shade(index) {
  const mix = Math.min(index / Math.max(props.steps.length - 1, 1), 1);
  const from = [28, 72, 72];
  const to = [236, 215, 159];
  const channel = (i) => Math.round(from[i] + (to[i] - from[i]) * mix);
  return `rgb(${channel(0)}, ${channel(1)}, ${channel(2)})`;
}

const worstStep = computed(() => {
  if (props.steps.length < 2) return null;
  return props.steps
    .slice(1)
    .reduce((worst, step) => (!worst || step.conversion_from_previous < worst.conversion_from_previous ? step : worst), null);
});

function formatNumber(value) {
  return new Intl.NumberFormat('en-US').format(value ?? 0);
}
</script>
